import React, { useState } from "react";
import "./ProfileContent.css";
import useAuthentication from "../../../hooks/auth/UseAuthentification";
import EmailIcon from "@mui/icons-material/Email";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { assets } from "../../../publics/assets";
import { Collapse } from "react-collapse";
import { RotateLoader } from "react-spinners";
import { Box } from "@mui/material";
import AvatarUser from "../../atoms/avatarUser/AvatarUser";
import ConfirmationModal from "../confirmationModal/ConfiramationModal";
import CustomForm from "../customForm/CustomForm";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import { useTranslation } from "react-i18next";
const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL;

const ProfileContent = ({
  logout,
  updateProfile,
  updatePassword,
  triggerTooltip,
  isSmallScreen,
  openSection,
}) => {
  const { t } = useTranslation();
  const auth = useAuthentication();
  const { canAccessLayer } = useAccessRights();
  const canOpenDashboard = canAccessLayer("auth", "login_dashboard");
  const [isEditable, setIsEditable] = useState(false);
  const [changePwd, setChangePwd] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    id: auth.user.id,
    username: auth.user.username,
    email: auth.user.email,
    name: auth.user.name,
    lastname: auth.user.lastname,
    adress: auth.user.adress,
    country: auth.user.country,
    city: auth.user.city,
    organization: auth.user.organization,
    role: auth.user.role,
  });
  const [passwordData, setPasswordData] = useState({
    currentPwd: "",
    newPwd: "",
    confirmedPwd: "",
  });
  const openAdminDashboard = () => {
    window.open(DASHBOARD_URL, "_blank", "noopener,noreferrer");
  };
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  // Password validation function
  const validatePassword = (password) => {
    const minLength = 8;
    const upperCasePattern = /[A-Z]/;
    const lowerCasePattern = /[a-z]/;
    const numberPattern = /[0-9]/;
    const specialCharPattern = /[!@#$%^&*(),.?":{}|<>]/;

    if (password.length < minLength) {
      triggerTooltip(t("profile.errors.pwdTooShort"));
      return false;
    }
    if (!upperCasePattern.test(password)) {
      triggerTooltip(t("profile.errors.pwdNoUpper"));
      return false;
    }
    if (!lowerCasePattern.test(password)) {
      triggerTooltip(t("profile.errors.pwdNoLower"));
      return false;
    }
    if (!numberPattern.test(password)) {
      triggerTooltip(t("profile.errors.pwdNoNumber"));
      return false;
    }
    if (!specialCharPattern.test(password)) {
      triggerTooltip(t("profile.errors.pwdNoSpecial"));
      return false;
    }
    return true; // Valid password
  };

  // Handle password change
  const handleSavePassword = async (e) => {
    //e.preventDefault();

    // Check if the password fields are not empty
    if (
      !passwordData.currentPwd ||
      !passwordData.newPwd ||
      !passwordData.confirmedPwd
    ) {
      triggerTooltip(t("profile.errors.emptyFields"));
      return;
    }

    // Validate new password
    if (!validatePassword(passwordData.newPwd)) {
      return; //if validation fails and prevent form submission
    }

    // Check if new password and confirmed password match
    if (passwordData.newPwd !== passwordData.confirmedPwd) {
      triggerTooltip(t("profile.errors.pwdMismatch"));
      return;
    }

    try {
      const updatedPassword = await updatePassword(
        passwordData.currentPwd,
        passwordData.newPwd,
        auth.user.id,
        auth.accessToken,
      );
      setChangePwd(false);
      setPasswordData({ currentPwd: "", newPwd: "", confirmedPwd: "" }); // Reset fields
    } catch (error) {
      console.error("Failed to update password:", error);
    }
  };

  // Handle form submission (sending to backend the modifies userdata)
  const handleSubmitProfileModif = async (e) => {
    //e.preventDefault();  Prevent default form submission

    try {
      const updatedUser = await updateProfile(formData, auth.accessToken);
      setIsEditable(false); // Disable editing after saving
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };
  const handleSaveClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    if (changePwd) {
      handleSavePassword();
    } else {
      handleSubmitProfileModif();
    }
    setShowConfirmation(false);
  };

  const handleCancelConfirm = () => {
    setShowConfirmation(false);
    if (changePwd) {
      resetPwdModification();
    } else {
      resetProfileModification();
    }
  };
  const resetProfileModification = () => {
    setIsEditable(false);
    setFormData({
      id: auth.user.id,
      username: auth.user.username,
      email: auth.user.email,
      name: auth.user.name,
      lastname: auth.user.lastname,
      adress: auth.user.adress,
      country: auth.user.country,
      city: auth.user.city,
      organization: auth.user.organization,
      password: "",
      role: auth.user.role,
    });
  };
  const resetPwdModification = () => {
    setPasswordData({
      currentPwd: "",
      newPwd: "",
      confirmedPwd: "",
    });
    setChangePwd(false);
  };
  const passwordFormInputs = [
    [
      {
        label: t("profile.password.currentLabel"),
        name: "currentPwd",
        type: "text",
        className: "profile-input-style",
        value: passwordData.currentPwd,
        onChange: handlePasswordChange,
        placeholder: t("profile.password.currentPlaceholder"),
      },
    ],
    [
      {
        label: t("profile.password.newLabel"),
        name: "newPwd",
        type: "text",
        className: "profile-input-style",
        value: passwordData.newPwd,
        onChange: handlePasswordChange,
        placeholder: t("profile.password.newPlaceholder"),
      },
    ],
    [
      {
        label: t("profile.password.confirmLabel"),
        name: "confirmedPwd",
        type: "text",
        className: "profile-input-style",
        value: passwordData.confirmedPwd,
        onChange: handlePasswordChange,
        placeholder: t("profile.password.confirmPlaceholder"),
      },
    ],
  ];

  const profileFormInputs = [
    [
      {
        label: t("profile.fields.username"),
        name: "username",
        type: "text",
        className: "profile-input-style",
        value: formData.username,
        onChange: handleChange,
        disabled: !isEditable,
      },
      {
        label: t("profile.fields.email"),
        name: "email",
        type: "email",
        className: "profile-input-style",
        value: formData.email,
        onChange: handleChange,
        disabled: !isEditable,
      },
    ],
    [
      {
        label: t("profile.fields.lastname"),
        name: "lastname",
        type: "text",
        className: "profile-input-style",
        value: formData.lastname,
        onChange: handleChange,
        disabled: !isEditable,
      },
      {
        label: t("profile.fields.name"),
        name: "name",
        type: "text",
        className: "profile-input-style",
        value: formData.name,
        onChange: handleChange,
        disabled: !isEditable,
      },
    ],
    [
      {
        label: t("profile.fields.organization"),
        name: "organization",
        type: "text",
        className: "profile-input-style",
        value: formData.organization,
        onChange: handleChange,
        disabled: !isEditable,
      },
      {
        label: t("profile.fields.role"),
        name: "role",
        type: "text",
        className: "profile-input-style",
        value: formData.role,
        onChange: handleChange,
        disabled: !isEditable,
      },
    ],
    [
      {
        label: t("profile.fields.adress"),
        name: "adress",
        type: "text",
        className: "profile-input-style",
        value: formData.adress,
        onChange: handleChange,
        disabled: !isEditable,
      },
    ],
    [
      {
        label: t("profile.fields.country"),
        name: "country",
        type: "text",
        className: "profile-input-style",
        value: formData.country,
        onChange: handleChange,
        disabled: !isEditable,
      },
      {
        label: t("profile.fields.city"),
        name: "city",
        type: "text",
        className: "profile-input-style",
        value: formData.city,
        onChange: handleChange,
        disabled: !isEditable,
      },
    ],
  ];
  return (
    <>
      {isSmallScreen ? (
        <div>
          <Collapse isOpened={openSection === "profile"}>
            {!auth.user ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginBottom="30%"
              >
                <RotateLoader color="#b10511" loading={true} size={20} />{" "}
              </Box>
            ) : (
              <div className="profile-container">
                <div className="user-infos-container">
                  <div className="user-infos-photo">
                    <AvatarUser />
                    {/*<img
                      src={assets.userPhoto}
                      alt="logo"
                      className="avatar-img"
                    />*/}
                  </div>
                </div>
                {!changePwd ? (
                  <CustomForm
                    formClassName="profile-form"
                    inputsConfig={profileFormInputs}
                  />
                ) : (
                  <CustomForm
                    formClassName="pwd-form"
                    inputsConfig={passwordFormInputs}
                  />
                )}

                <div className="profile-btns">
                  {!(isEditable || changePwd) ? (
                    <>
                      {canOpenDashboard && (
                        <button
                          className="profile-dashboard-btn"
                          onClick={openAdminDashboard}
                          data-testid="profile-dashboard-button"
                        >
                          {t("profile.buttons.dashboard")}
                          <OpenInNewIcon style={{ fontSize: "16px" }} />
                        </button>
                      )}
                      <button
                        className="profile-edit-btn"
                        onClick={() => setChangePwd(true)}
                        data-testid="profile-change-password-button"
                      >
                        {t("profile.buttons.changePassword")}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="profile-edit-btn"
                        onClick={handleSaveClick}
                        data-testid="profile-save-button"
                      >
                        {t("profile.buttons.save")}{" "}
                      </button>
                      <button
                        className="profile-logout-btn"
                        onClick={() =>
                          changePwd
                            ? resetPwdModification()
                            : resetProfileModification()
                        }
                        data-testid="profile-cancel-button"
                      >
                        {t("profile.buttons.cancel")}{" "}
                        <ArrowForwardIcon
                          style={{ fontSize: "16px", marginLeft: "4px" }}
                        />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </Collapse>
        </div>
      ) : (
        <div>
          {!auth.user ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginBottom="30%"
            >
              <RotateLoader color="#b10511" loading={true} size={20} />{" "}
            </Box>
          ) : (
            <div className="profile-container">
              <div className="user-infos-container">
                <div className="user-infos-photo">
                  <AvatarUser />
                  {/* <img
                    src={assets.userPhoto}
                    alt="logo"
                    className="avatar-img"
                  /> */}
                </div>
                <div className="user-infos">
                  <div className="user-infos-item">
                    <AccountCircleIcon style={{ fontSize: "18px" }} />
                    <p>{auth.user.username}</p>
                  </div>
                  <div className="user-infos-item">
                    <HomeRepairServiceIcon style={{ fontSize: "18px" }} />
                    <p>{auth.user.role}</p>
                  </div>
                  <div className="user-infos-item">
                    <EmailIcon style={{ fontSize: "18px" }} />
                    <p>{auth.user.email}</p>
                  </div>
                  {canOpenDashboard && (
                    <button
                      className="profile-dashboard-btn"
                      onClick={openAdminDashboard}
                      data-testid="profile-dashboard-button"
                    >
                      {t("profile.buttons.dashboard")}
                      <OpenInNewIcon style={{ fontSize: "16px" }} />
                    </button>
                  )}
                </div>
              </div>
              {!changePwd ? (
                <CustomForm
                  formClassName="profile-form"
                  inputsConfig={profileFormInputs}
                />
              ) : (
                <CustomForm
                  formClassName="pwd-form"
                  inputsConfig={passwordFormInputs}
                />
              )}

              <div className="profile-btns">
                {!(isEditable || changePwd) ? (
                  <>
                    <button
                      className="profile-edit-btn"
                      onClick={() => setChangePwd(true)}
                      data-testid="profile-change-password-button"
                    >
                      {t("profile.buttons.changePassword")}
                    </button>
                    <button
                      className="profile-logout-btn"
                      onClick={logout}
                      data-testid="profile-logout-button"
                    >
                      {t("profile.buttons.logout")}
                      <ArrowForwardIcon
                        style={{ fontSize: "16px", marginLeft: "4px" }}
                      />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="profile-edit-btn"
                      onClick={handleSaveClick}
                      data-testid="profile-save-button"
                    >
                      {t("profile.buttons.save")}
                    </button>
                    <button
                      className="profile-logout-btn"
                      onClick={() =>
                        changePwd
                          ? resetPwdModification()
                          : resetProfileModification()
                      }
                      data-testid="profile-cancel-button"
                    >
                      {t("profile.buttons.cancel")}
                      <ArrowForwardIcon
                        style={{ fontSize: "16px", marginLeft: "4px" }}
                      />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <ConfirmationModal
        open={showConfirmation}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        title={t("profile.confirmation.title")}
        message={t("profile.confirmation.message")}
      />
    </>
  );
};

export default ProfileContent;
